import { Card } from "@/components/ui/card";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import { Banknote, TrendingUp, Receipt, Timer, Layers, TrendingDown } from "lucide-react";

export const COLORS = { plan: "#94a3b8", booking: "#0ea5e9", billing: "#10b981", hd: "#a3231b", vmt: "#f59e0b", red: "#dc2626" };
export const cr2 = (v) => `${Number(v || 0).toFixed(2)}`;
export const r2 = (v) => +Number(v || 0).toFixed(2);

export function ChartCard({ title, children, height = 260, testId, subtitle }) {
  return (
    <Card className="p-4" data-testid={testId}>
      <h3 className="font-heading font-semibold text-sm text-slate-800">{title}</h3>
      {subtitle && <p className="text-[11px] text-slate-400 mb-1">{subtitle}</p>}
      <div style={{ height }} className="mt-2">{children}</div>
    </Card>
  );
}

const tooltipStyle = { fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" };

function Kpi({ icon: Icon, label, value, sub, accent, testId }) {
  return (
    <Card className="p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
        <span className="h-7 w-7 rounded-md flex items-center justify-center" style={{ backgroundColor: `${accent}18` }}>
          <Icon className="h-3.5 w-3.5" style={{ color: accent }} />
        </span>
      </div>
      <p className="font-mono text-xl font-bold text-slate-900">{value}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
    </Card>
  );
}

export function MISOverview({ dash, deals, losses, product }) {
  const pick = (r, base) => (product === "HD" ? r[`${base}_hd`] : product === "VMT" ? r[`${base}_vmt`] : r[`${base}_hd`] + r[`${base}_vmt`]);
  const sum = (fn) => dash.reduce((a, r) => a + fn(r), 0);

  const plan = sum((r) => r.sales_plan);
  const booking = sum((r) => r.ytd_booking);
  const billing = sum((r) => r.ytd_billing);
  const pending = sum((r) => pick(r, "pending"));
  const pipeline = deals.reduce((a, d) => a + d.value_lakhs, 0) / 100;
  const lost = losses.reduce((a, w) => a + w.value_lakhs, 0) / 100;
  const pct = (v) => (plan ? `${Math.round((v / plan) * 100)}% of annual plan` : "");

  const byBranch = dash.map((r) => ({
    name: `${r.branch}${r.segment !== "CRE" ? ` ${r.segment}` : ""}`,
    Plan: r2(r.sales_plan), Booking: r2(r.ytd_booking), Billing: r2(r.ytd_billing),
  }));
  const quarters = [
    { q: "Q1", Plan: r2(sum((r) => pick(r, "q1_plan"))), Actual: r2(sum((r) => pick(r, "q1_act"))) },
    { q: "Q2", Plan: r2(sum((r) => pick(r, "q2_plan"))), Actual: r2(sum((r) => pick(r, "q2_act"))) },
    { q: "Q3", Plan: r2(sum((r) => pick(r, "q3_plan"))), Actual: 0 },
    { q: "Q4", Plan: r2(sum((r) => pick(r, "q4_plan"))), Actual: 0 },
  ];
  const months = [
    { m: "June", "Billing Plan": r2(sum((r) => r.jun_billing_plan)), "Billing Actual": null, "Booking Plan": r2(sum((r) => r.jun_booking_plan)), "Booking Actual": null },
    { m: "July", "Billing Plan": r2(sum((r) => r.jul_billing_plan)), "Billing Actual": r2(sum((r) => r.jul_billing_act)), "Booking Plan": r2(sum((r) => r.jul_booking_plan)), "Booking Actual": r2(sum((r) => r.jul_booking_act)) },
    { m: "August", "Billing Plan": r2(sum((r) => r.aug_billing_plan)), "Billing Actual": r2(sum((r) => r.aug_billing_act)), "Booking Plan": r2(sum((r) => r.aug_booking_plan)), "Booking Actual": r2(sum((r) => r.aug_booking_act)) },
  ];
  const mix = [
    { name: "HD Panels", value: r2(sum((r) => r.q1_act_hd + r.q2_act_hd)) },
    { name: "VMT", value: r2(sum((r) => r.q1_act_vmt + r.q2_act_vmt)) },
  ];

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-5">
        <Kpi icon={Banknote} label="Annual Plan" value={cr2(plan)} accent="#64748b" testId="mis-kpi-plan" />
        <Kpi icon={TrendingUp} label="YTD Booking" value={cr2(booking)} sub={pct(booking)} accent={COLORS.booking} testId="mis-kpi-booking" />
        <Kpi icon={Receipt} label="YTD Billing" value={cr2(billing)} sub={pct(billing)} accent={COLORS.billing} testId="mis-kpi-billing" />
        <Kpi icon={Timer} label="Pending Orders" value={cr2(pending)} accent="#f59e0b" testId="mis-kpi-pending" />
        <Kpi icon={Layers} label="Deals Pipeline" value={cr2(pipeline)} sub={`${deals.length} large deals`} accent="#a3231b" testId="mis-kpi-pipeline" />
        <Kpi icon={TrendingDown} label="Lost YTD" value={cr2(lost)} sub={`${losses.length} projects`} accent={COLORS.red} testId="mis-kpi-lost" />
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mb-5">
        <div className="lg:col-span-2">
          <ChartCard title="Plan vs Actual by branch (INR Cr)" subtitle="Annual plan vs YTD booking & billing" testId="mis-chart-branch" height={280}>
            <ResponsiveContainer>
              <BarChart data={byBranch} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-28} textAnchor="end" height={54} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Plan" fill={COLORS.plan} radius={[3, 3, 0, 0]} />
                <Bar dataKey="Booking" fill={COLORS.booking} radius={[3, 3, 0, 0]} />
                <Bar dataKey="Billing" fill={COLORS.billing} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
        <ChartCard title="Product mix — booked (INR Cr)" subtitle="HD Panels vs VMT actuals" testId="mis-chart-mix" height={280}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={mix} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                <Cell fill={COLORS.hd} /><Cell fill={COLORS.vmt} />
              </Pie>
              <Tooltip contentStyle={tooltipStyle} formatter={(v) => cr2(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid lg:grid-cols-2 gap-5 mb-5">
        <ChartCard title="Quarterly trajectory (INR Cr)" subtitle="Plan vs actual per quarter" testId="mis-chart-quarters">
          <ResponsiveContainer>
            <BarChart data={quarters} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="q" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Plan" fill={COLORS.plan} radius={[3, 3, 0, 0]} />
              <Bar dataKey="Actual" fill={COLORS.booking} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Monthly billing & booking (INR Cr)" subtitle="Plan vs actual — June / July / August" testId="mis-chart-months">
          <ResponsiveContainer>
            <BarChart data={months} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="m" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Billing Plan" fill="#a7f3d0" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Billing Actual" fill={COLORS.billing} radius={[3, 3, 0, 0]} />
              <Bar dataKey="Booking Plan" fill="#bae6fd" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Booking Actual" fill={COLORS.booking} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </>
  );
}
